import React, { useMemo, useState, useCallback } from 'react';
import { FlatTreeNode } from 'types';
import { COLORS, Button } from 'pvs-design-system';
import { Box } from '@mui/material';
import { TreeNode, TreeNodeItem, type TreeNodeItemProps } from './TreeSelectNode';

// ── Build nested tree from flat array ────────────────────────────────
function buildTree(nodes: FlatTreeNode[], excludeId: number | null = null): TreeNode[] {
  const map = new Map<number, TreeNode>();
  const roots: TreeNode[] = [];

  const excludedSet = new Set<number>();
  if (excludeId !== null) {
    const collectExcluded = (id: number) => {
      excludedSet.add(id);
      nodes.forEach((n) => {
        if (n.idParent === id) collectExcluded(n.id);
      });
    };
    collectExcluded(excludeId);
  }

  nodes.forEach((n) => {
    if (!excludedSet.has(n.id)) {
      map.set(n.id, { ...n, children: [] });
    }
  });

  map.forEach((node) => {
    if (node.idParent === null || !map.has(node.idParent)) {
      roots.push(node);
    } else {
      map.get(node.idParent)!.children.push(node);
    }
  });

  return roots;
}

// ── Compute ancestor IDs for a node ─────────────────────────────────
function getAncestorIds(nodes: FlatTreeNode[], targetId: number | null | undefined): Set<number> {
  if (targetId == null) return new Set();
  const map = new Map(nodes.map((n) => [n.id, n]));
  const ancestors = new Set<number>();
  let current = map.get(targetId);
  while (current && current.idParent != null) {
    ancestors.add(current.idParent);
    current = map.get(current.idParent);
  }
  return ancestors;
}

// ── Public props ───────────────────────────────────────────────────────

interface BaseTreeSelectProps {
  /** Flat list of nodes from the API */
  nodes: FlatTreeNode[];
  /**
   * When set, this node and all its descendants are hidden.
   * Used in edit-mode to prevent circular parent selection.
   */
  excludeId?: number | null;
  /**
   * 'dropdown' (default): rendered as a trigger button that opens a floating panel.
   * 'inline': tree is always visible with no trigger — used inside modals/panels.
   */
  variant?: 'dropdown' | 'inline';
  placeholder?: string;
  /**
   * Optional lazy-load callback for expanding nodes with hasChildren=true.
   * Called when user expands a node whose children haven't been fetched yet.
   * The parent should fetch children from the API and merge them into `nodes`.
   */
  onLoadChildren?: (nodeId: number) => Promise<void>;
  /** When true, display locationType in brackets after each node name */
  showLocationType?: boolean;
  /** When true, append a [selected/total] sublocation count after each node name */
  showCount?: boolean;
  /** Ids counted as "selected" for the [selected/total] count (defaults to the
   *  multi-select selectedIds). Lets callers count cascade-excluded descendants. */
  countIds?: Set<number>;
  /** All flat nodes — needed for descendant count with lazy-loaded trees */
  allFlatNodes?: FlatTreeNode[];
}

/** Single-select mode */
interface SingleSelectProps extends BaseTreeSelectProps {
  multiSelect?: false;
  selectedId: number | null;
  onSelect: (id: number) => void;
  selectedIds?: never;
  onToggle?: never;
}

/** Multi-select mode (checkboxes) */
interface MultiSelectProps extends BaseTreeSelectProps {
  multiSelect: true;
  selectedIds: Set<number>;
  onToggle: (id: number) => void;
  selectedId?: never;
  onSelect?: never;
}

export type TreeSelectProps = SingleSelectProps | MultiSelectProps;

const TreeSelect: React.FC<TreeSelectProps> = (props) => {
  const { nodes, excludeId = null, variant = 'dropdown', placeholder = 'Select…', onLoadChildren, showLocationType = false, showCount = false, countIds, allFlatNodes } = props;
  const [open, setOpen] = useState(false);

  // Extract optional callbacks so useCallback can reference them without the
  // discriminated-union props object changing identity on every render.
  const onSelectSingle = !props.multiSelect ? props.onSelect : undefined;
  const onToggleMulti  = props.multiSelect  ? props.onToggle : undefined;

  const tree = useMemo(() => buildTree(nodes, excludeId), [nodes, excludeId]);

  // Compute which ancestors need to be auto-expanded to reveal the selected node
  const selectedId = props.multiSelect ? undefined : props.selectedId;
  const expandedAncestors = useMemo(
    () => getAncestorIds(nodes, selectedId),
    [nodes, selectedId],
  );

  // Label shown on the trigger button
  const triggerLabel = props.multiSelect
    ? props.selectedIds.size > 0
      ? `${props.selectedIds.size} selected`
      : placeholder
    : (nodes.find((n) => n.id === props.selectedId)?.name ?? null);

  const handleSelect = useCallback((id: number) => {
    onSelectSingle?.(id);
    setOpen(false);
  }, [onSelectSingle]);

  const handleToggle = useCallback((id: number) => {
    onToggleMulti?.(id);
  }, [onToggleMulti]);

  const treeBody = (
    tree.length === 0 ? (
      <p style={{ padding: '12px 16px', color: COLORS.gray[400], fontSize: 16, margin: 0 }}>No options available.</p>
    ) : (
      <ul style={{ listStyle: 'none', margin: 0, padding: '4px 0' }} role="tree">
        {tree.map((node) => (
          <TreeNodeItem
            key={node.id}
            node={node}
            selectedId={props.multiSelect ? undefined : props.selectedId}
            onSelect={!props.multiSelect ? handleSelect : undefined}
            multiSelect={props.multiSelect}
            selectedIds={props.multiSelect ? props.selectedIds : undefined}
            onToggle={props.multiSelect ? handleToggle : undefined}
            depth={0}
            // inline trees start with the root level expanded
            startExpanded={variant === 'inline'}
            onLoadChildren={onLoadChildren}
            expandedAncestors={expandedAncestors}
            showLocationType={showLocationType}
            showCount={showCount}
            countIds={countIds}
            allFlatNodes={allFlatNodes}
          />
        ))}
      </ul>
    )
  );

  // ── Inline variant: always-visible tree (no trigger button) ──────────
  if (variant === 'inline') {
    return <Box style={{ width: '100%' }}>{treeBody}</Box>;
  }

  // ── Dropdown variant ───────────────────────────────────────────────────────────
  return (
    <Box style={{ position: 'relative', width: '100%' }}>
      <Button
        variant="text"
        aria-haspopup="tree"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '6px 10px',
          background: '#fff',
          border: `2px solid ${open ? COLORS.purple[700] : COLORS.gray[200]}`,
          borderRadius: '4px',
          fontSize: 16,
          color: COLORS.gray[900],
          textAlign: 'left',
          textTransform: 'none',
          fontWeight: 400,
          transition: 'border-color 0.15s',
          minWidth: 0,
          '&:hover': { background: '#fff', borderColor: COLORS.purple[700] },
        }}
      >
        <span style={triggerLabel ? {} : { color: COLORS.gray[400] }}>
          {triggerLabel ?? placeholder}
        </span>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s ease' }}>
          <path d="M2 4 L6 8 L10 4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Button>

      {open && (
        <>
          <Box style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setOpen(false)} />
          <Box style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 100,
            background: '#fff',
            border: `1px solid ${COLORS.gray[200]}`,
            borderRadius: 4,
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            maxHeight: 280,
            overflowY: 'auto' as const,
          }}>
            {treeBody}
          </Box>
        </>
      )}
    </Box>
  );
};

export default React.memo(TreeSelect);
